import type { MergeFieldsConfig } from 'ckeditor5-premium-features';
import type GlobalUI from './types';
import { AuditRoles, EntityTreeFilter, FSLIType, OrganisationsView, ReportType } from 'utils/enum';

const initialState: GlobalUI = {
   activeDashboardTab: undefined,
   tabEditMode: false,

   kpiEditMode: false,
   taxonomyEditMode: false,
   groupsEditMode: false,
   footprintEditMode: false,

   showModalCreateOrUpdateChart: false,
   showModalAddChartItem: false,
   showDashboardSidebar: false,
   showOutOfScopeEmissionItems: false,

   contentSidebarInfoExpanded: false,
   contentSidebarCommentsExpanded: false,
   contentSidebarTasksExpanded: false,
   contentSidebarAttachmentsExpanded: false,
   contentSidebarFieldLinksExpanded: false,

   showModalTopicScreening: false,
   showModalSupplierAssessmentCreateOrUpdate: false,
   showModalSupplierSiteCreateOrUpdate: false,
   showModalSupplierCreate: false,
   showModalSettingsEntities: false,
   showModalForgotPassword: false,
   showModalSignup: false,
   showModalEditColumnKPIMeta: false,
   showModalKPIScopeDialogGRI: {
      open: false,
      from: null,
   },
   showModalKPIScopeDialogESRS: {
      open: false,
      from: null,
   },
   showModalKPIScopeDialog: false,
   showModalTaxonomyGuidanceEdit: false,
   showModalTaxonomyAuditorGuidanceEdit: false,
   showModalRenameColumn: false,
   showModalManageFSLIs: false,
   showModalSubsidiaryValues: false,
   showModalKPIAreaCreateOrUpdate: false,
   showMoveUsersToNewOrgDialog: false,
   showModalKPICreateOrUpdate: false,
   showModalKPISubCreate: false,
   showModalKPISubUpdate: false,
   showModalKPISubCreateOrUpdate: false,
   showModalKPIContentCreateOrUpdate: false,
   showModalKPIFieldCreate: false,
   showModalKPIFieldCreateOrUpdate: false,
   showModalKPIFieldEdit: false,
   showModalCreateOrUpdateComment: false,
   showModalUpdateCurrencies: false,
   showModalUserCreate: false,
   showModalUserCreatePassword: false,
   showModalCreateRule: false,
   showModalKPIHeaderInfo: false,
   showModalKPISubHeaderInfo: {
      open: false,
      id: null,
   },
   showModalKPIValueVersions: false,
   showModalKPIValueItems: false,
   showModalCreateReportOrDocument: false,
   showModalEditReportOrDocument: false,
   showModalMaterialityScoping: false,
   showModalTaxonomyAssignmentReason: false,
   showModalDataImport: {
      open: false,
      type: 'default',
      id: null,
      url: null,
      isLiveTemplateFootprint: false,
   },
   showModalImportDefinition: false,
   showModalDashboardAddTab: false,
   showModalCreateGroupMember: false,
   showModalTaxonomyInfo: false,
   showModalFSLICreateOrUpdate: false,
   showModalCreateOrUpdateBusinessActivities: false,
   showModalCreateOrUpdateFootprint: false,
   showModalCreateOrUpdateLiveTemplate: false,
   showModalRollOutLiveTemplate: false,
   showModalCreateOrUpdateCalculation: false,
   showModalCalculationInfo: {
      open: false,
      id: null,
   },
   showModalEmissionMetada: false,
   showModalPeriodSelection: false,
   showModalOrganisationSelection: false,
   showModalWidgetCreateOrUpdate: false,
   showModalFullChangelog: false,
   showContentSidebar: false,
   showHistorySidebar: false,
   showAvatarCropper: false,
   showEmissionFactorsSidebar: false,
   showConversionFactorsSidebar: false,
   showModalEmissionCategoryCreateOrUpdate: false,
   showModalEmissionFactorCreateOrUpdate: false,
   showModalConversionFactorCreateOrUpdate: false,
   showModalEmissionSourceCreateOrUpdate: false,
   showModalAISuggestEmissionFactors: false,
   showModalFootprintSubCreateOrUpdate: false,
   showModalFootprintSubDelete: {
      open: false,
      footprintSubId: null,
   },
   showModalSelectCRAProvider: false,
   showModalEditLoginOptions: false,
   showLoadingOverlay: false,
   showModalCreateOrUpdateCalculationField: false,
   showCreateTaskModal: false,
   showEditTaskModal: false,
   showCreateActionModal: false,
   showModalTaskStaffModel: false,
   showModalProductCreateOrUpdate: false,
   showModalMaterialCreateOrUpdate: false,
   showModalValidationRules: {
      open: false,
      type: null,
      id: null,
      isBlocked: false,
   },
   showModalGenericEntityAuditors: {
      open: false,
      role: AuditRoles.AUDITOR,
   },
   showModalRejectAuditItem: {
      open: false,
      type: null,
      id: null,
   },
   showModalRollForwardPeriod: false,
   showModalClosingPeriod: false,
   showModalKPIFieldGroupRowEdit: false,
   showModalKPIFieldNameColumnEdit: false,
   showModalBusinessActivityExport: false,
   showModalKPIFieldNotApplicableCreateOrUpdate: false,
   showModalIROCreateOrUpdate: false,
   showModalAttachmentCreateOrUpdate: false,
   showModalPerformScoping: false,
   showModalPerformBusinessActivityScoping: false,
   showModalStrategyCreateOrUpdate: false,
   showModalStrategyTargetCreateOrUpdate: false,
   showModalStrategyTargetActionCreateOrUpdate: false,
   strategyModuleViewState: 'list',

   showModalGeneralRiskMitigationTaskCreateOrUpdate: false,
   showModalGeneralRiskMitigationControlCreateOrUpdate: false,
   showModalGeneralRiskCreateOrUpdate: false,
   showModalGeneralRiskMitigationCreateOrUpdate: false,
   modalGeneralRiskStepAssignees: {
      open: false,
      variant: null,
   },
   modalRiskAffectedItemCreateOrUpdate: {
      open: false,
      variant: null,
   },

   showModalGeneralRiskAddExisting: {
      open: false,
      type: null,
   },
   showModalClimateRisksCreateOrEdit: false,
   showModalClimateRiskSegmentsCreateOrEdit: false,
   showModalGeneralRiskCategoryCreateOrUpdate: {
      open: false,
      category: null,
   },
   modalGeneralRiskAssessmentsCreateOrUpdate: {
      open: false,
      assessment: null,
   },
   generalRiskModuleMitigationsViewState: 'tiles',

   modalSettingsPeriods: {
      open: false,
      type: null,
   },
   modalSettingsStandards: {
      open: false,
      type: null,
   },
   modalSettingsDocumentTemplates: {
      open: false,
      type: null,
   },
   modalDocumentCreateOrUpdate: {
      open: false,
      type: null,
   },
   modalReportCreateOrUpdate: {
      open: false,
      type: null,
   },
   modalSettingsUserGroups: {
      type: null,
      open: false,
      templateGroup: null,
   },
   modalSettingsTags: {
      open: false,
      type: null,
   },

   organisationsView: OrganisationsView.OrganisationList,
   organisationsViewTopbar: null,
   taxonomyDisclosureType: FSLIType.REVENUE,
   taxonomyDisclosureCutOffDate: undefined,
   templateVersion: 'old',

   kpiShowInfo: false,
   mainNavigationOpen: false,

   modalTaxonomyInfoId: undefined,
   activeModalTaskStaffModel: undefined,
   activeReportingStandardId: undefined,

   tutorialStep: 0,

   searchTaxonomyList: '',
   searchTasksList: '',
   searchSupplierSiteList: '',
   searchSupplierMaterialList: '',
   profileEditMode: false,
   showUpdateTaskDialog: false,
   showCreateEditGoalModal: false,
   showCreateUpdateSupplyChainActionModal: false,

   reportOnlyApproved: false,
   reportShowEmptyFields: false,
   reportFullConsolidation: false,
   reportGroupByOrganisation: false,
   reportUsePeriodTitle: false,
   reportType: ReportType.REGULAR,
   emissionReportType: ReportType.GHG,
   keyFigureReportReportingStandardIds: [],
   keyFigureTagIds: [],
   keyFigureOrganisationIds: [],
   keyFigureKpiAreaIds: [],
   keyFigureKpiIds: [],
   keyFigureKpiSubIds: [],
   keyFigureExpandAll: null,
   emissionsReportFootprintIdentifier: undefined,

   comparativeEmissionsChartLevelDropdownSelectedOptions: [],

   showAllDNSHCriteria: true,

   showSupplyChainProductView: false,
   showModalEmissionTemplateImport: false,
   taskEditComment: false,
   genericAttachmentModalContext: null,
   showAddSupplierModal: false,
   showSelectOwnCompanyEntitiesDialog: false,
   showModalSupplierComplaintCreateOrEdit: false,
   showModalSupplierCaseCreateOrEdit: false,
   showModalSupplierActionCreateOrEdit: false,
   editingSupplierId: null,
   searchSupplierList: '',
   searchProductList: '',
   searchMaterialList: '',
   showDeletedUser: false,
   showDeletedImportJobs: false,
   showModalIROsSuggest: false,
   showModalSegmentAddOrEdit: false,

   isKPIValueItemsTableDirty: false,
   statisticsFilters: {
      periods: [],
      reportingStandards: [],
      organisations: [],
      kpiSubs: [],
      status: [],
      tags: [],
   },
   KPITableFieldInGridMode: false,
   orgaTreeSortOrder: {},
   reportEditorMergeFieldsPreviewMode: '$labels' as keyof MergeFieldsConfig['previewModes'],
   showModalClimateRiskActionsCreateOrEdit: false,
   showModalAnnouncementCreateOrUpdate: false,

   questionnaireCreatorContext: {
      open: false,
      mode: 'create',
      entity: null,
   },

   entityTreeSearchQuery: '',
   entityTreeFilter: EntityTreeFilter.DEFAULT,
   entityTreeHighlightedNodeId: null,
   entityTreeSelectedNodePath: [],
};

export default initialState;
